import { useState, useCallback, useMemo } from 'react';
import { BlueprintLocation } from '../../types/protocols';
import { StorageUnit, StorageUnitInput } from '../../types/storageUnits';
import { storageUnitsApi } from '../../api/storageUnitsApi';
import { sortedStorageUnits } from '../../Helpers/storageUnitUtils';
import { useBuilding } from '../../Helpers/BuildingContext';

interface UseStorageUnitsProps {
    buildingKey: string | null | undefined;
}

type PhotoUploader = (config: { storagePath: (photoKey: string) => string; dbPath: (photoKey: string) => string }) => Promise<unknown>;

interface UseStorageUnitsReturn {
    units: { [unitKey: string]: StorageUnit };
    /** Every unit in natural number order. */
    sortedUnits: StorageUnit[];
    loading: boolean;
    error: string | null;
    submitting: boolean;
    createUnit: (input: StorageUnitInput, location?: BlueprintLocation) => Promise<StorageUnit>;
    updateUnit: (unitKey: string, input: StorageUnitInput) => Promise<void>;
    setUnitLocation: (unitKey: string, imageKey: string, location: BlueprintLocation | null) => Promise<void>;
    deleteUnit: (unitKey: string) => Promise<void>;
    deleteUnitPhoto: (unitKey: string, photoKey: string) => Promise<void>;
    /**
     * Uploads unit photos: verifies admin, hands the path config to the
     * caller's uploader (usePhotoUpload.uploadPhotos), stamps the unit, refetches.
     */
    uploadUnitPhotos: (unitKey: string, uploader: PhotoUploader) => Promise<void>;
    refresh: () => Promise<void>;
    clearError: () => void;
}

/**
 * Storage units of the current building. The data comes from the already
 * loaded BuildingContext (`model.storageUnits`); BuildingContext has no
 * per-subtree patch, so every mutation refetches the building afterwards
 * (plan D13, same as useParking).
 */
export const useStorageUnits = ({ buildingKey }: UseStorageUnitsProps): UseStorageUnitsReturn => {
    const { currentBuilding, loadingBuilding, fetchBuilding } = useBuilding();
    const [error, setError] = useState<string | null>(null);
    const [submitting, setSubmitting] = useState(false);

    const units = useMemo(() => {
        if (!currentBuilding || currentBuilding.key !== buildingKey) return {};
        return storageUnitsApi.parseUnits(currentBuilding.model.storageUnits);
    }, [currentBuilding, buildingKey]);

    const sortedUnits = useMemo(() => sortedStorageUnits(units), [units]);

    const refresh = useCallback(async () => {
        if (buildingKey) {
            await fetchBuilding(buildingKey);
        }
    }, [buildingKey, fetchBuilding]);

    const run = useCallback(async <T,>(fallbackMessage: string, action: () => Promise<T>): Promise<T> => {
        if (!buildingKey) {
            const message = 'Ingen byggnad vald';
            setError(message);
            throw new Error(message);
        }
        setSubmitting(true);
        setError(null);
        try {
            const result = await action();
            await refresh();
            return result;
        } catch (err) {
            console.error(fallbackMessage, err);
            setError(err instanceof Error ? err.message : fallbackMessage);
            throw err;
        } finally {
            setSubmitting(false);
        }
    }, [buildingKey, refresh]);

    const createUnit = useCallback((input: StorageUnitInput, location?: BlueprintLocation) =>
        run('Kunde inte skapa förrådet', () => storageUnitsApi.createUnit(buildingKey!, input, location)),
    [run, buildingKey]);

    const updateUnit = useCallback((unitKey: string, input: StorageUnitInput) =>
        run('Kunde inte uppdatera förrådet', () => storageUnitsApi.updateUnit(buildingKey!, unitKey, input)),
    [run, buildingKey]);

    const setUnitLocation = useCallback((unitKey: string, imageKey: string, location: BlueprintLocation | null) =>
        run('Kunde inte placera förrådet', () => storageUnitsApi.setUnitLocation(buildingKey!, unitKey, imageKey, location)),
    [run, buildingKey]);

    const deleteUnit = useCallback((unitKey: string) =>
        run('Kunde inte ta bort förrådet', () => storageUnitsApi.deleteUnit(buildingKey!, unitKey)),
    [run, buildingKey]);

    const deleteUnitPhoto = useCallback((unitKey: string, photoKey: string) =>
        run('Kunde inte ta bort fotot', () => storageUnitsApi.deleteUnitPhoto(buildingKey!, unitKey, photoKey)),
    [run, buildingKey]);

    const uploadUnitPhotos = useCallback((unitKey: string, uploader: PhotoUploader) =>
        run('Kunde inte ladda upp foton', async () => {
            const config = await storageUnitsApi.prepareUnitPhotoUpload(buildingKey!, unitKey);
            await uploader(config);
            await storageUnitsApi.stampUnitUpdated(buildingKey!, unitKey);
        }),
    [run, buildingKey]);

    const clearError = useCallback(() => setError(null), []);

    return {
        units,
        sortedUnits,
        loading: loadingBuilding,
        error,
        submitting,
        createUnit,
        updateUnit,
        setUnitLocation,
        deleteUnit,
        deleteUnitPhoto,
        uploadUnitPhotos,
        refresh,
        clearError,
    };
};
